import qs from 'qs';
import formatDynamicNavigation from '@/search-for-retail/dynamic-navigation/dynamic-navigation.formatters';
import parseDynamicNavigation from '@/search-for-retail/dynamic-navigation/dynamic-navigation.parsers';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path segment for the dynamic navigation resource under an area. */
const DYNAMIC_NAVIGATION_PATH = 'navigation';
// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------
/**
 * RTK Query endpoints for dynamic navigation data.
 *
 * Dynamic navigation endpoints sit under `/{areaId}/navigation`.
 * Auth and tenant headers are handled by the base `ccApi`.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const dynamicNavigationApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        // -----------------------------------------------------------------------
        // Dynamic navigation queries
        // -----------------------------------------------------------------------
        /**
         * Fetches the list of dynamic navigations for an area.
         *
         * @endpoint GET /{areaId}/navigation
         * @returns Parsed DynamicNavigation[] via transformResponse
         */
        getDynamicNavigationList: builder.query({
            query: ({ areaId }) => `/${areaId}/${DYNAMIC_NAVIGATION_PATH}`,
            transformResponse: (response) => response === null || response === void 0 ? void 0 : response.map(parseDynamicNavigation),
            providesTags: [CC_API_TAGS.DynamicNavigationList],
        }),
        /**
         * Fetches a single dynamic navigation by ID.
         *
         * @endpoint GET /{areaId}/navigation/{id}
         * @returns Parsed DynamicNavigation via transformResponse
         */
        getDynamicNavigationById: builder.query({
            query: ({ areaId, id }) => `/${areaId}/${DYNAMIC_NAVIGATION_PATH}/${id}`,
            transformResponse: (response) => parseDynamicNavigation(response),
            providesTags: (_result, _error, { id }) => [
                { type: CC_API_TAGS.DynamicNavigation, id },
            ],
        }),
        // -----------------------------------------------------------------------
        // Dynamic navigation mutations
        // -----------------------------------------------------------------------
        /**
         * Creates a new dynamic navigation in an area.
         *
         * @endpoint POST /{areaId}/navigation
         * @returns Parsed DynamicNavigation via transformResponse
         */
        createDynamicNavigation: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${DYNAMIC_NAVIGATION_PATH}`,
                method: 'POST',
                body: formatDynamicNavigation(Object.assign({ areaId }, data)),
            }),
            transformResponse: (response) => parseDynamicNavigation(response),
            invalidatesTags: [CC_API_TAGS.DynamicNavigationList],
        }),
        /**
         * Updates an existing dynamic navigation.
         *
         * @endpoint PUT /{areaId}/navigation/{id}
         * @returns Parsed DynamicNavigation via transformResponse
         */
        updateDynamicNavigation: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${DYNAMIC_NAVIGATION_PATH}/${data.id}`,
                method: 'PUT',
                body: formatDynamicNavigation(Object.assign({ areaId }, data)),
            }),
            transformResponse: (response) => parseDynamicNavigation(response),
            invalidatesTags: (_result, _error, { data }) => [
                CC_API_TAGS.DynamicNavigationList,
                { type: CC_API_TAGS.DynamicNavigation, id: data.id },
            ],
        }),
        /**
         * Deletes a single dynamic navigation from an area.
         *
         * @endpoint DELETE /{areaId}/navigation/{id}
         */
        deleteDynamicNavigation: builder.mutation({
            query: ({ areaId, id }) => ({
                url: `/${areaId}/${DYNAMIC_NAVIGATION_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => [
                CC_API_TAGS.DynamicNavigationList,
                { type: CC_API_TAGS.DynamicNavigation, id },
            ],
        }),
        /**
         * Bulk-deletes dynamic navigations from an area.
         *
         * @endpoint DELETE /{areaId}/navigation/bulk/delete?ids=...
         */
        bulkDeleteDynamicNavigation: builder.mutation({
            query: ({ areaId, idList }) => {
                const query = qs.stringify({ ids: idList }, { indices: false });
                return {
                    url: `/${areaId}/${DYNAMIC_NAVIGATION_PATH}/bulk/delete?${query}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: [CC_API_TAGS.DynamicNavigationList],
        }),
        /**
         * Promotes a dynamic navigation to another area.
         *
         * @endpoint POST /{areaId}/navigation/{id}/promote/{targetAreaId}
         */
        promoteDynamicNavigationToArea: builder.mutation({
            query: ({ areaId, targetAreaId, id }) => ({
                url: `/${areaId}/${DYNAMIC_NAVIGATION_PATH}/${id}/promote/${targetAreaId}`,
                method: 'POST',
            }),
            invalidatesTags: [CC_API_TAGS.DynamicNavigationList],
        }),
        /**
         * Bulk-promotes dynamic navigations to another area.
         *
         * @endpoint POST /{areaId}/navigation/bulk/promote/{targetAreaId}?ids=...
         */
        bulkPromoteDynamicNavigationToArea: builder.mutation({
            query: ({ areaId, targetAreaId, idList }) => {
                const query = qs.stringify({ ids: idList }, { indices: false });
                return {
                    url: `/${areaId}/${DYNAMIC_NAVIGATION_PATH}/bulk/promote/${targetAreaId}?${query}`,
                    method: 'POST',
                };
            },
            invalidatesTags: [CC_API_TAGS.DynamicNavigationList],
        }),
    }),
});
export const { useGetDynamicNavigationListQuery, useLazyGetDynamicNavigationListQuery, useGetDynamicNavigationByIdQuery, useLazyGetDynamicNavigationByIdQuery, useCreateDynamicNavigationMutation, useUpdateDynamicNavigationMutation, useDeleteDynamicNavigationMutation, useBulkDeleteDynamicNavigationMutation, usePromoteDynamicNavigationToAreaMutation, useBulkPromoteDynamicNavigationToAreaMutation, } = dynamicNavigationApi;
